/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import debounce from 'lodash.debounce';
import * as React from 'react';
import { addDomEventListener } from 'src/core/utils/domEventListener';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';
import { getLifetime } from 'src/core/utils/lifetime';
import { createReducer } from 'src/luis/modules/labeling/utils/generalUtils';
import { Label, MultilineLabel } from 'src/modules/labeling/types/Label';
import { tokenIndexDataAttribute } from 'src/modules/labeling/utils/labelingConstants';
import { resizeReducer } from 'src/modules/labeling/utils/resizeReducer';

/**
 * A hook for adding the behavior of resizing labels and predictions.
 */
export const useResizeBehavior = (labels: Label[], onLabelResize: (label: Label) => void) => {
    const [resizedLabels, setResizedLabels] = React.useState(labels);
    const [isResizing, setIsResizing] = React.useState(false);

    React.useEffect(() => {
        setResizedLabels(labels);
    }, [labels]);

    const onResize = React.useCallback((label: MultilineLabel, knob: 'start' | 'end') => {
        const lifetime = getLifetime();
        const { dispatch, getState, getDefaultState } = createReducer(resizeReducer, { knob, label });
        setIsResizing(true);

        const cancel = () => {
            lifetime.dispose();
            setIsResizing(false);
        };

        const revert = (e: KeyboardEvent) => {
            if (e.code === LuisKeyCodes.Escape) {
                const {
                    label: { end, start }
                } = getDefaultState();
                label.originalLabel.start = start;
                label.originalLabel.end = end;

                setResizedLabels(_labels => _labels.slice());
                cancel();
            }
        };

        const resize = debounce((e: MouseEvent) => {
            const targetElements = document.elementsFromPoint(e.x, e.y);
            const token = targetElements.find(element => element.hasAttribute(tokenIndexDataAttribute));
            if (!token) {
                return;
            }

            const tokenIndex = parseInt(token.getAttribute(tokenIndexDataAttribute), 10);

            dispatch({ tokenIndex });
            const {
                label: { start, end },
                knob: newKnob
            } = getState();

            let requiresUpdate = false;

            if (newKnob === 'start' && label.originalLabel.start !== start) {
                label.originalLabel.start = start;
                requiresUpdate = true;
            } else if (newKnob === 'end' && label.originalLabel.end !== end) {
                label.originalLabel.end = end;
                requiresUpdate = true;
            }

            if (requiresUpdate) {
                setResizedLabels(_labels => _labels.slice());
            }
        }, 60);

        addDomEventListener(lifetime, document.body, 'keyup', revert);
        addDomEventListener(lifetime, document.body, 'mouseleave', cancel);
        addDomEventListener(lifetime, document.body, 'mouseup', e => {
            e.stopPropagation();
            cancel();
            onLabelResize(label.originalLabel);
        });
        addDomEventListener(lifetime, document.body, 'mousemove', resize);
    }, []);

    return { resizedLabels, onResize, isResizing };
};
